import React, { useState, useRef } from "react";
import { Modal, Form } from "react-bootstrap";
import Loader from "../../common/Loader";
import delivaApiService from "../../../common/services/apiService";
import "./AddOrganization.scss";
import { useEffect } from "react";
import { showErrorToast } from "../../../common/helpers/Utils";

const AddOrganization = ({ orgId, mode, onHide, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [organizationName, setOrganizationName] = useState("");
  const [organizationEmail, setOrganizationEmail] = useState("");
  const [initialData, setInitialData] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [paymentTransferTo, setPaymentTransferTo] =
    useState("DELIVERY_PARTNER_ADMIN");
  const [errors, setErrors] = useState({});
  const isMountedRef = useRef(true);

  useEffect(() => {
    const fetchOrganizationDetails = async () => {
      if (mode === "edit" && orgId) {
        await getOrganizationDetailsForEdit(orgId);
      }
    };

    fetchOrganizationDetails();

    return () => {
      isMountedRef.current = false;
    };
  }, [orgId, mode]);

  useEffect(() => {
    if (mode !== "edit" || !initialData) return;

    const hasChanged =
      organizationName.trim() !== initialData.name ||
      organizationEmail.trim() !== initialData.email ||
      paymentTransferTo.trim() !== initialData.paymentTransferTo;

    setIsDirty(hasChanged);
  }, [
    organizationName,
    organizationEmail,
    paymentTransferTo,
    initialData,
    mode,
  ]);

  const validateForm = () => {
    const newErrors = {};

    if (!organizationName.trim()) {
      newErrors.organizationName = "Organization name is required";
    }

    const emailRegex =
      /^(?!.*(\.[a-zA-Z]{2,})(?:\1)+)[a-zA-Z0-9._%+-]+@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
    const emailFormatRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,6}$/;

    if (!organizationEmail.trim()) {
      newErrors.organizationEmail = "Organization email is required";
    } else if (!emailRegex.test(organizationEmail) || !emailFormatRegex.test(organizationEmail)) {
      newErrors.organizationEmail = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getOrganizationDetailsForEdit = async (orgId) => {
    try {
      setLoading(true);
      const payload = {
        orgId: orgId,
      };
      const { data: response } = await delivaApiService(
        "getOrganizationDetails",
        payload,
      );

      // Only update state if component is still mounted
      if (isMountedRef.current && response?.status === 200) {
        let data = response?.resourceData?.resourceData;
        const initial = {
          name: data?.name || "",
          email: data?.email || "",
          paymentTransferTo: data?.paymentTransferTo,
        };

        setOrganizationName(initial.name);
        setOrganizationEmail(initial.email);
        setPaymentTransferTo(initial.paymentTransferTo);

        setInitialData(initial);
        setIsDirty(false);

        // Set the form fields with the fetched data
        setOrganizationName(data?.name || "");
        setOrganizationEmail(data?.email || "");
        setPaymentTransferTo(data?.paymentTransferTo);
      }
    } catch (error) {
      console.error("Error fetching organization details:", error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleCreateOrganization = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const formData = {
        name: organizationName,
        email: organizationEmail,
        paymentTransferTo: paymentTransferTo,
        employeeRole: "DELIVERY_PARTNER",
      };

      const response = await delivaApiService("addOrganization", formData);

      if (isMountedRef.current) {
        if (response?.status === 200 && response?.data?.status === 200) {
          onSuccess();
          onHide();
        } else {
          showErrorToast(
            response?.data?.responseMessage ||
              "Failed to create organization. Please try again.",
          );
        }
      }
    } catch (err) {
      console.error("Error creating organization:", err);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleEditOrganization = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const formData = {
        orgId: orgId,
        name: organizationName,
        email: organizationEmail,
        paymentTransferTo: paymentTransferTo,
      };

      // Uncomment when API is ready
      const response = await delivaApiService("editOrganization", formData);

      if (isMountedRef.current) {
        if (response?.status === 200 && response?.data?.status === 200) {
          onSuccess();
          onHide();
        } else {
          showErrorToast(
            response?.data?.message ||
              response?.data?.responseMessage ||
              "Failed to update organization. Please try again.",
          );
        }
      }
    } catch (err) {
      console.error("Error updating organization:", err);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  return (
    <Modal
      show={true}
      centered
      size="lg"
      onHide={() => null}
      className="add-organization-modal"
    >
      <Modal.Header className="border-bottom">
        <Modal.Title className="fw-normal">
          {mode === "edit" ? "Edit Organization" : "Add Organization"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Loader showLoader={loading} />
        <Form className="add-organization-form">
          {/* Organization Name and Email */}
          <div className="row mb-4">
            <div className="col-md-6">
              <Form.Group className="mb-0">
                <Form.Label htmlFor="organizationName">
                  <h6 className="fw-bold mb-2">Organization Name</h6>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Name"
                  value={organizationName}
                  name="organizationName"
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                    setOrganizationName(value);
                  }}
                  maxLength={50}
                  className="login-input "
                />
                {errors?.organizationName && (
                  <span className="text-danger mt-2">
                    {errors?.organizationName}
                  </span>
                )}
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-0">
                <Form.Label htmlFor="organizationEmail">
                  <h6 className="fw-bold mb-2">Organization Email</h6>
                </Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Email"
                  id="organizationEmail"
                  value={organizationEmail}
                  onChange={(e) => setOrganizationEmail(e.target.value)}
                  maxLength={50}
                  className="login-input "
                />
                {errors?.organizationEmail && (
                  <span className="text-danger mt-2">
                    {errors?.organizationEmail}
                  </span>
                )}
              </Form.Group>
            </div>
          </div>

          {/* Payment Transfer To */}
          <div className="mb-4">
            <Form.Label>
              <h3 className="font-weight-normal mb-3 line-ht color-33 fs20">
                Payment transfer to
              </h3>
            </Form.Label>
            <div className="payment-transfer-options">
              <div className="checkboxContainer">
                <input
                  id="only_delivery_partner_admin"
                  type="radio"
                  name="paymentTransfer"
                  value="DELIVERY_PARTNER_ADMIN"
                  checked={paymentTransferTo === "DELIVERY_PARTNER_ADMIN"}
                  onChange={(e) => setPaymentTransferTo(e.target.value)}
                />
                <label htmlFor="only_delivery_partner_admin">
                  {" "}
                  {"Delivery Partner Admin"}
                </label>
              </div>
              <div className="checkboxContainer">
                <input
                  id="delivery_partner"
                  type="radio"
                  name="paymentTransfer"
                  value="DELIVERY_PARTNER"
                  checked={paymentTransferTo === "DELIVERY_PARTNER"}
                  onChange={(e) => setPaymentTransferTo(e.target.value)}
                />
                <label htmlFor="delivery_partner"> {"Delivery Partner"}</label>
              </div>
              
            </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer className="border-top d-flex justify-content-start">
        <button
          variant="outline-secondary"
          onClick={onHide}
          className="button btnDefault btnOutline text-nowrap px-4 py-2"
        >
          Cancel
        </button>
        <button
          variant="success"
          onClick={
            mode === "edit" ? handleEditOrganization : handleCreateOrganization
          }
          disabled={mode === "edit" && !isDirty}
          className="button btnDefault btnPrimary text-nowrap px-4 py-2"
        >
          {mode === "edit" ? "Save" : "Create"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddOrganization;
